import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe (move this to an environment variable)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

console.log('Stripe Key:', process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function PaymentForm({ data, onNext, onBack, clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      setError('Stripe has not been properly initialized');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // 1. Submit the form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw submitError;
      }

      // 2. Create payment method
      const { paymentMethod, error: paymentError } = await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            address: {
              city: data.addresses?.billing?.city,
              country: data.addresses?.billing?.country,
              line1: data.addresses?.billing?.street,
              postal_code: data.addresses?.billing?.zipCode,
              state: data.addresses?.billing?.state,
            },
          },
        },
      });

      if (paymentError) {
        throw paymentError;
      }

      // 3. Pass payment method and client secret to next step
      onNext({
        paymentMethod: {
          id: paymentMethod.id,
          last4: paymentMethod.card.last4,
          brand: paymentMethod.card.brand,
          expMonth: paymentMethod.card.exp_month,
          expYear: paymentMethod.card.exp_year,
          clientSecret,
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Payment Information
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Your payment information is securely processed by Stripe.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <PaymentElement 
          options={{
            layout: 'tabs',
            wallets: {
              applePay: 'auto',
              googlePay: 'auto',
            },
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          onClick={onBack}
          disabled={processing}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!stripe || processing}
        >
          {processing ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              Processing...
            </>
          ) : (
            'Next'
          )}
        </Button>
      </Box>
    </Box>
  );
}

// Wrapper component to handle Stripe Elements initialization
function PaymentSetup({ data, onNext, onBack }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [setupError, setSetupError] = useState(null);

  // 1. Fetch client secret when component mounts
  useEffect(() => {
    const fetchClientSecret = async () => {
      try {
        console.log('Fetching client secret with data:', {
          plan: data.subscription?.plan,
          interval: data.subscription?.interval,
        });

        // 2. Make API call to create payment intent
        const response = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            plan: data.subscription?.plan,
            interval: data.subscription?.interval,
          }),
        });

        const responseData = await response.json();
        
        if (!response.ok) {
          throw new Error(responseData.message || 'Failed to initialize payment');
        }

        // 3. Store the client secret
        console.log('Client secret received');
        setClientSecret(responseData.clientSecret);
      } catch (error) {
        console.error('Payment setup error:', error);
        setSetupError(error.message);
      }
    };

    if (data.subscription?.plan) {
      fetchClientSecret();
    }
  }, [data.subscription]);

  // Show error state
  if (setupError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {setupError}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button onClick={onBack}>Back</Button>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  // Show loading state
  if (!clientSecret) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Only render Elements when we have a client secret
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#5c2d91',
            colorBackground: '#ffffff',
            colorText: '#30313d',
          },
        },
        paymentMethodCreation: 'manual',
      }}
    >
      <PaymentForm 
        data={data} 
        onNext={onNext} 
        onBack={onBack}
        clientSecret={clientSecret}
      />
    </Elements>
  );
}

// Export both the main component and a wrapped version for the review step
export const PaymentElementsProvider = ({ children, clientSecret }) => {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#5c2d91',
            colorBackground: '#ffffff',
            colorText: '#30313d',
          },
        },
        paymentMethodCreation: 'manual',
      }}
    >
      {children}
    </Elements>
  );
};

export default PaymentSetup; 